import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service, Timeframe } from 'webapp-types';
import { ForecastMethod } from 'webapp/enums/enums';

import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ForecastsPopupsLinearChangeComponent from 'webapp/components/forecasts/popups/linear-change';
import { LinearChangePattern } from 'webapp/components/forecasts/settings/linear-change/input';

import type { ForecastMethodology } from 'webapp/models-custom/forecast';

interface Args {
	selectedMethodology: ForecastMethodology;
	timeframe: Timeframe | null;
	endDate: string | null;
	isDisabled: boolean;
	onChange?: (methodology: ForecastMethodology) => void;
}

export default class ForecastsSettingsRegressorMethodologyComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;

	@tracked selectedMethod: ForecastMethod;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.selectedMethod = this.args.selectedMethodology.type;
	}

	get methodologyOptions() {
		const databoxLabel = this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
			? 'Advanced (recommended)'
			: 'Databox (recommended)';

		return [
			{
				action: ForecastMethod.DATABOX,
				text: databoxLabel,
			},
			{
				action: ForecastMethod.SIMPLE_EXPONENTIAL,
				text: 'Simple exponential smoothing',
			},
			{
				action: ForecastMethod.LINEAR_CHANGE,
				text: 'Linear change',
			},
			{
				action: ForecastMethod.MOVING_AVERAGE_6M,
				text: '6-months moving average',
			},
			//TODO @marcel uncomment when supported on BE
			// {
			// 	action: ForecastMethod.AVERAGE,
			// 	text: 'Average',
			// },
		];
	}

	get selectedMethodText() {
		const option = this.methodologyOptions.find((opt) => opt.action === this.selectedMethod);
		return option?.text || 'Forecasting methodology';
	}

	private getLinearChangePopupData(): PopupData.Forecast.LinearChange {
		// Get current values from the methodology
		const currentValue = this.args.selectedMethodology?.extra?.changeValue || null;
		const currentPattern =
			(this.args.selectedMethodology?.extra?.changePattern as LinearChangePattern) ||
			LinearChangePattern.MONTHLY_PERCENT;

		return {
			value: currentValue,
			changePattern: currentPattern,
			timeframe: this.args.timeframe,
			endDate: this.args.endDate,
			onApply: (value: number | null, changePattern: LinearChangePattern) => {
				this.selectedMethod = ForecastMethod.LINEAR_CHANGE;
				const methodology: ForecastMethodology = {
					type: ForecastMethod.LINEAR_CHANGE,
					extra: {
						changeValue: value ?? undefined,
						changePattern: changePattern,
					},
				};

				if (value !== null && value !== undefined) {
					this.args.onChange?.(methodology);
				}
			},
		};
	}

	@action
	openLinearChangeModal() {
		const modal = this.popupService.createModal(ForecastsPopupsLinearChangeComponent, {
			allowStacked: true,
			popupClass: 'popup-type-linear-change',
			showClose: true,
		});
		modal.open(this.getLinearChangePopupData());
	}

	@action
	openMethodologyPopup(event: Event) {
		if (this.args.isDisabled) {
			return;
		}

		const element = event.target as HTMLElement;
		const popup = this.popupService.createListPopup(element, '', '', true, true);

		if (popup) {
			popup.open({
				itemsList: this.methodologyOptions,
				doAction: (actionName?: string) => {
					void popup.close();
					if (actionName === ForecastMethod.LINEAR_CHANGE) {
						this.openLinearChangeModal();
					} else if (actionName && actionName !== this.selectedMethod) {
						this.selectedMethod = actionName as ForecastMethod;
						this.args.onChange?.({
							type: actionName as ForecastMethod,
							extra: null,
						});
					}
				},
			});
		}
	}
	<template>
		<a
			role="button"
			class={{joinClasses
				(array "regressor-methodology" (if @isDisabled "regressor-methodology--disabled"))
			}}
			{{on "click" this.openMethodologyPopup}}
		>
			<span class="regressor-methodology__text">{{this.selectedMethodText}}</span>
			<CommonUiIconComponent
				@name="ChevronDown"
				@size="xs"
				@class="regressor-methodology__arrow"
			/>
		</a>
	</template>
}
